import * as React from "react";
import { useNavigate } from "react-router";

import type {
    HomePromptOption,
    RecommendedAssessmentsResponse,
} from "@volley/data";

import { fetchApi } from "../../util";
import { logFetchError } from "../../util/fetchApi";
import { useAssessmentStart } from "../Journeys/PerformanceDashboard/useAssessmentStart";
import { inProgressUrl } from "../Journeys/journeyNav";
import type { Sport } from "../common/context/sport";
import useJourney from "../hooks/useJourney";

import { ASSESSMENTS_ROUTE, FINISH_ASSESSMENT_VALUE } from "./constants";

export interface AssessmentLaunch {
    /**
     * Drive the assessment flow for an assessment-launch option (see
     * `isAssessmentLaunchOption`). "Finish" resumes the in-progress assessment
     * directly; "start" (and any finish with nothing in progress) opens the
     * all-skills assessment start dialog.
     */
    launchAssessment: (option: HomePromptOption, sport: Sport) => Promise<void>;
    /** The assessment start dialog element to render. */
    dialog: React.JSX.Element;
}

/**
 * Bridges the Ask Volley prompts to the existing assessment start/resume
 * machinery so assessment options skip the chat and act immediately. Owns the
 * shared AssessmentStartDialog (via useAssessmentStart) and the resume
 * navigation, mirroring how the dialog itself resumes a single- vs multi-workout
 * assessment.
 */
export function useAssessmentLaunch(): AssessmentLaunch {
    const navigate = useNavigate();
    const { openStart, dialog } = useAssessmentStart();
    const { activeJourneys, registerJourneyLinkIntent } = useJourney();

    // Resume the user's in-progress assessment directly, returning false when
    // there is none to resume (so the caller can fall back to starting one).
    const resumeInProgressAssessment =
        React.useCallback(async (): Promise<boolean> => {
            const instance = activeJourneys.find(
                (j) => j.journey.type === "assessment",
            );
            if (!instance) return false;

            // Single-workout assessment: go straight to play. Intent must be
            // registered first so the play links back to this journey slot.
            if (instance.workoutsPlays.length === 1) {
                const aw = instance.workoutsPlays[0].appWorkout;
                await registerJourneyLinkIntent(instance.ulid, aw.id);
                navigate(
                    `/content/apps/workouts/plugin/play/${aw.appId}/${aw.id}`,
                );
                return true;
            }

            // Multi-workout assessment: resume on the in-progress page.
            navigate(
                inProgressUrl({
                    journeyType: "assessment",
                    journeyInstanceId: instance.id,
                    journeyInstanceUlid: instance.ulid,
                }),
            );
            return true;
        }, [activeJourneys, registerJourneyLinkIntent, navigate]);

    // Open the start dialog for the sport's all-skills assessment.
    const startAllSkillsAssessment = React.useCallback(
        async (sport: Sport): Promise<void> => {
            try {
                const res = await fetchApi<RecommendedAssessmentsResponse>(
                    `/api/journeys/performance/v2/recommended-assessments?sport=${sport}`,
                );
                if (res.allSkillsAssessment) {
                    openStart(res.allSkillsAssessment.journeyId);
                    return;
                }
                // Only pickleball has a released all-skills assessment, so for
                // every other sport this is the normal outcome, not an error.
                // Land on the assessments list rather than doing nothing, which
                // reads as a broken chip.
                navigate(ASSESSMENTS_ROUTE);
            } catch (err) {
                logFetchError(err, "Failed to load the all-skills assessment");
                navigate(ASSESSMENTS_ROUTE);
            }
        },
        [openStart, navigate],
    );

    const launchAssessment = React.useCallback(
        async (option: HomePromptOption, sport: Sport): Promise<void> => {
            if (option.value === FINISH_ASSESSMENT_VALUE) {
                const resumed = await resumeInProgressAssessment();
                if (resumed) return;
                // Nothing in progress (state drifted since the prompt was
                // served): fall through to offering a fresh assessment.
            }
            await startAllSkillsAssessment(sport);
        },
        [resumeInProgressAssessment, startAllSkillsAssessment],
    );

    return { launchAssessment, dialog };
}
